import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "@/i18n"
import { useFeatures } from "@/config/context"
import { useProfiles } from "@/hooks/useProfiles"
import { usePublish } from "@/hooks/usePublish"
import { useFollowing } from "@/hooks/useSocial"
import { KINDS, type NostrEvent } from "@/nostr/types"
import { useAuth } from "@/state/AuthContext"
import { useRelays } from "@/state/RelayContext"
import { useToast } from "@/state/ToastContext"
import {
  firstLine,
  loadDrafts,
  newDraftId,
  removeDraft,
  storeDrafts,
  upsertDraft,
  type NoteDraft,
} from "@/utils/drafts"
import { npubOf } from "@/utils/nip19"
import { simplifyUrl } from "@/utils/url"
import { Icon } from "./Icon"
import { LoginDialog } from "./LoginDialog"
import { Modal } from "./Modal"
import { NoteContent } from "./NoteContent"

const MAX_LENGTH = 8000
const MAX_THREAD_NOTES = 4
const PLACEHOLDER = "Write something… (Paste or drop media files to upload)"

/** A small, keyboard reachable emoji set (inserted at the caret). */
const EMOJI = [
  "😀", "😂", "🥰", "😎", "🤔", "😅", "😭", "🙌", "👍", "👀",
  "🔥", "💜", "✨", "🎉", "🚀", "🌍", "☕", "🍕", "🎸", "📚",
  "🧠", "💡", "🛠️", "⚡", "🌱", "🌙", "☀️", "🌈", "🐝", "🦊",
  "🍻", "🎧", "📡", "🧵", "🫶", "🤝", "👏", "💯", "🕯️", "🪄",
]

type Popover = "media" | "emoji" | "relays" | "settings" | "mentions" | null
type RelayMode = "optimal" | "custom"

export interface NoteComposerDialogProps {
  open: boolean
  onClose: () => void
  /** Called after a successful publish (e.g. to refresh a feed). */
  onPublished?: () => void
}

/**
 * "New Note" dialog — Jumble's composer.
 *
 * Edit/Preview tabs, paste-or-drop media, a NIP-70 "Protected" flag, the relay
 * selection ("Optimal relays"), a local drafts list, emoji/mention/thread tools
 * and post settings (content warning). Publishing posts every thread note in
 * order, threading each one under the previous note.
 */
export function NoteComposerDialog({ open, onClose, onPublished }: NoteComposerDialogProps) {
  const { t } = useTranslation()
  const features = useFeatures()
  const { canSign, pubkey } = useAuth()
  const { writeTargets } = useRelays()
  const { publishNote } = usePublish()
  const { push } = useToast()
  const following = useFollowing()

  const [content, setContent] = useState("")
  const [tab, setTab] = useState<"edit" | "preview">("edit")
  const [threadNotes, setThreadNotes] = useState<string[]>([])
  const [isProtected, setIsProtected] = useState(false)
  const [contentWarning, setContentWarning] = useState("")
  const [relayMode, setRelayMode] = useState<RelayMode>("optimal")
  const [customRelays, setCustomRelays] = useState<string[]>([])
  const [popover, setPopover] = useState<Popover>(null)
  const [showProtectedHelp, setShowProtectedHelp] = useState(false)
  const [mediaUrl, setMediaUrl] = useState("")
  const [mentionQuery, setMentionQuery] = useState("")
  const [drafts, setDrafts] = useState<NoteDraft[]>([])
  const [showDrafts, setShowDrafts] = useState(false)
  const [draftId, setDraftId] = useState<string>(() => newDraftId())
  const [publishing, setPublishing] = useState(false)
  const [hint, setHint] = useState<string>()
  const [showLogin, setShowLogin] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const targets = useMemo(
    () => (relayMode === "custom" ? customRelays.filter((url) => writeTargets.includes(url)) : writeTargets),
    [relayMode, customRelays, writeTargets],
  )

  const notes = useMemo(
    () => [content, ...threadNotes].map((note) => note.trim()).filter(Boolean),
    [content, threadNotes],
  )

  const mentionCandidates = useMemo(() => following.following.slice(0, 60), [following.following])
  const mentionProfiles = useProfiles(mentionCandidates)
  const mentions = useMemo(() => {
    const term = mentionQuery.trim().toLowerCase()
    return mentionCandidates
      .map((candidate) => ({ pubkey: candidate, npub: npubOf(candidate), profile: mentionProfiles.get(candidate) }))
      .filter((entry) => {
        if (!term) return true
        const name = `${entry.profile?.displayName ?? ""} ${entry.profile?.name ?? ""}`.toLowerCase()
        return name.includes(term) || entry.npub.toLowerCase().includes(term)
      })
      .slice(0, 30)
  }, [mentionCandidates, mentionProfiles, mentionQuery])

  const previewEvent = useMemo<NostrEvent>(
    () => ({
      id: "preview",
      pubkey: pubkey ?? "0".repeat(64),
      created_at: Math.floor(Date.now() / 1000),
      kind: KINDS.NOTE,
      tags: [],
      content,
      sig: "",
    }),
    [content, pubkey],
  )

  // Restore the drafts list whenever the dialog opens.
  useEffect(() => {
    if (open) setDrafts(loadDrafts())
  }, [open])

  const persistDrafts = (next: NoteDraft[]) => {
    setDrafts(next)
    storeDrafts(next)
  }

  const close = () => {
    if (content.trim()) {
      persistDrafts(upsertDraft(loadDrafts(), { id: draftId, content, updatedAt: Date.now() }))
    }
    setPopover(null)
    setShowDrafts(false)
    onClose()
  }

  const insert = (snippet: string) => {
    const element = textareaRef.current
    if (!element) {
      setContent((current) => (current + snippet).slice(0, MAX_LENGTH))
      return
    }
    const start = element.selectionStart ?? content.length
    const end = element.selectionEnd ?? start
    const next = (content.slice(0, start) + snippet + content.slice(end)).slice(0, MAX_LENGTH)
    setContent(next)
    window.requestAnimationFrame(() => {
      element.focus()
      const caret = start + snippet.length
      element.setSelectionRange(caret, caret)
    })
  }

  const handleFiles = (files: FileList | File[]) => {
    if ([...files].length === 0) return
    setHint(
      t("Media uploads are not configured in this build — paste a hosted image, video or audio URL instead."),
    )
    setPopover("media")
  }

  const openDraft = (draft: NoteDraft) => {
    if (content.trim()) {
      persistDrafts(upsertDraft(loadDrafts(), { id: draftId, content, updatedAt: Date.now() }))
    }
    setContent(draft.content)
    setDraftId(draft.id)
    setThreadNotes([])
    setTab("edit")
    setShowDrafts(false)
  }

  const deleteDraft = (id: string) => {
    persistDrafts(removeDraft(drafts, id))
    if (id === draftId) setDraftId(newDraftId())
  }

  const post = async () => {
    if (notes.length === 0 || publishing) return
    if (!canSign) {
      setShowLogin(true)
      return
    }
    setPublishing(true)
    setHint(undefined)
    const extraTags: string[][] = []
    if (isProtected) extraTags.push(["-"])
    if (contentWarning.trim()) extraTags.push(["content-warning", contentWarning.trim()])

    let root: NostrEvent | undefined
    let previous: NostrEvent | undefined
    let failed = false
    for (const [index, note] of notes.entries()) {
      const outcome = await publishNote(note, {
        relays: targets,
        tags: extraTags,
        root,
        replyTo: previous,
        silent: notes.length > 1 && index < notes.length - 1,
      })
      if (!outcome.ok) {
        failed = true
        break
      }
      root = root ?? outcome.event
      previous = outcome.event
    }
    setPublishing(false)
    if (failed) return

    if (notes.length > 1) push("success", `${notes.length} notes published`)
    persistDrafts(removeDraft(loadDrafts(), draftId))
    setContent("")
    setThreadNotes([])
    setContentWarning("")
    setIsProtected(false)
    setDraftId(newDraftId())
    setTab("edit")
    onPublished?.()
    onClose()
  }


  const relayLabel = relayMode === "optimal" ? t("Optimal relays") : `${targets.length} ${t("relays")}`
  const relayCountLabel = targets.length === 1 ? t("1 relay") : `${targets.length} ${t("relays")}`
  const postDisabled = notes.length === 0 || publishing

  const footer = (
    <div className="composer-toolbar">
      <div className="composer-tools">
        <div className="composer-popover-wrap">
          <button
            type="button"
            className="composer-icon-button"
            aria-label={t("Add media")}
            aria-expanded={popover === "media"}
            title={t("Add media")}
            onClick={() => setPopover((value) => (value === "media" ? null : "media"))}
          >
            <Icon name="image" size={19} />
          </button>
          {popover === "media" && (
            <div className="composer-popover" role="group" aria-label={t("Add media")}>
              <input
                value={mediaUrl}
                onChange={(event) => setMediaUrl(event.target.value)}
                placeholder="https://…"
                aria-label={t("Media URL")}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" || !mediaUrl.trim()) return
                  event.preventDefault()
                  insert(mediaUrl.trim())
                  setMediaUrl("")
                  setPopover(null)
                }}
              />
              <div className="composer-popover-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-small"
                  disabled={!mediaUrl.trim()}
                  onClick={() => {
                    insert(mediaUrl.trim())
                    setMediaUrl("")
                    setPopover(null)
                  }}
                >
                  {t("Insert")}
                </button>
                <button type="button" className="btn btn-ghost btn-small" onClick={() => fileInputRef.current?.click()}>
                  {t("Choose file…")}
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*,audio/*"
                className="visually-hidden"
                aria-hidden="true"
                tabIndex={-1}
                onChange={(event) => {
                  if (event.target.files) handleFiles(event.target.files)
                  event.target.value = ""
                }}
              />
            </div>
          )}
        </div>

        <div className="composer-popover-wrap">
          <button
            type="button"
            className="composer-icon-button"
            aria-label={t("Add emoji")}
            aria-expanded={popover === "emoji"}
            title={t("Add emoji")}
            onClick={() => setPopover((value) => (value === "emoji" ? null : "emoji"))}
          >
            <Icon name="smile" size={19} />
          </button>
          {popover === "emoji" && (
            <div className="composer-popover composer-emoji-grid" role="group" aria-label={t("Emoji")}>
              {EMOJI.map((emoji) => (
                <button key={emoji} type="button" className="composer-emoji" aria-label={emoji} onClick={() => insert(emoji)}>
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          className="composer-icon-button"
          aria-label={t("Add a note to the thread")}
          title={t("Add a note to the thread")}
          disabled={threadNotes.length >= MAX_THREAD_NOTES}
          onClick={() => {
            setThreadNotes((current) => [...current, ""])
            setTab("edit")
          }}
        >
          <Icon name="notes" size={19} />
        </button>

        <button
          type="button"
          className="composer-icon-button"
          aria-label={t("Insert a bulleted list")}
          title={t("Insert a bulleted list")}
          onClick={() => insert(`• ${t("first point")}\n• ${t("second point")}`)}
        >
          <Icon name="list" size={19} />
        </button>

        <div className="composer-popover-wrap">
          <button
            type="button"
            className="composer-icon-button"
            aria-label={t("Post settings")}
            aria-expanded={popover === "settings"}
            title={t("Post settings")}
            onClick={() => setPopover((value) => (value === "settings" ? null : "settings"))}
          >
            <Icon name="settings" size={19} />
          </button>
          {popover === "settings" && (
            <div className="composer-popover composer-popover-wide" role="group" aria-label={t("Post settings")}>
              <label className="composer-field">
                <span>{t("Content warning")}</span>
                <input
                  value={contentWarning}
                  onChange={(event) => setContentWarning(event.target.value)}
                  placeholder={t("e.g. spoilers")}
                  maxLength={80}
                />
              </label>
              <label className="composer-check">
                <input type="checkbox" checked={isProtected} onChange={(event) => setIsProtected(event.target.checked)} />
                <span>{t("Protected note (NIP-70)")}</span>
              </label>
              <label className="composer-check">
                <input
                  type="checkbox"
                  checked={relayMode === "optimal"}
                  onChange={(event) => setRelayMode(event.target.checked ? "optimal" : "custom")}
                />
                <span>{t("Publish to optimal relays")}</span>
              </label>
            </div>
          )}
        </div>
      </div>


      <div className="composer-submit">
        <div className="composer-popover-wrap">
          <button
            type="button"
            className="composer-link"
            aria-expanded={popover === "mentions"}
            onClick={() => setPopover((value) => (value === "mentions" ? null : "mentions"))}
          >
            {t("Mentions")}
          </button>
          {popover === "mentions" && (
            <div className="composer-popover composer-popover-wide" role="group" aria-label={t("Mentions")}>
              <input
                value={mentionQuery}
                onChange={(event) => setMentionQuery(event.target.value)}
                placeholder={t("Search people you follow")}
                aria-label={t("Search people you follow")}
              />
              {mentions.length === 0 ? (
                <p className="muted small">{t("Follow people to mention them here.")}</p>
              ) : (
                <ul className="composer-mention-list">
                  {mentions.map((entry) => (
                    <li key={entry.pubkey}>
                      <button
                        type="button"
                        className="composer-mention"
                        onClick={() => {
                          insert(`nostr:${entry.npub} `)
                          setPopover(null)
                          setMentionQuery("")
                        }}
                      >
                        <span>{entry.profile?.displayName || entry.profile?.name || `${entry.npub.slice(0, 12)}…`}</span>
                        <small>{entry.profile?.nip05 || `${entry.npub.slice(0, 14)}…`}</small>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <button type="button" className="composer-link" onClick={close}>
          {t("Cancel")}
        </button>
        <button type="button" className="btn btn-primary composer-post" onClick={() => void post()} disabled={postDisabled}>
          {publishing ? t("Posting…") : t("Post")}
        </button>
      </div>
    </div>
  )

  if (!features.composer) return null

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={t("New Note")}
        size="wide"
        className="note-composer"
        showClose={false}
        footer={footer}
        headerAction={
          <button
            type="button"
            className={`composer-link composer-link-muted ${showDrafts ? "active" : ""}`}
            onClick={() => {
              setDrafts(loadDrafts())
              setShowDrafts((value) => !value)
              setPopover(null)
            }}
            aria-expanded={showDrafts}
          >
            {t("Drafts")}
            {drafts.length > 0 && <span className="composer-badge">{drafts.length}</span>}
          </button>
        }
      >
        {showDrafts && (
          <div className="composer-drafts" aria-label={t("Saved drafts")}>
            {drafts.length === 0 ? (
              <p className="muted small">{t("No drafts yet — notes you close without posting are saved here.")}</p>
            ) : (
              <ul>
                {drafts.map((draft) => (
                  <li key={draft.id} className="composer-draft">
                    <button type="button" className="composer-draft-open" onClick={() => openDraft(draft)}>
                      <span className="composer-draft-text">{firstLine(draft.content)}</span>
                      <span className="composer-draft-meta">
                        {new Date(draft.updatedAt).toLocaleString()} · {draft.content.length}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="icon-button small"
                      aria-label={t("Delete draft")}
                      onClick={() => deleteDraft(draft.id)}
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}


        {!canSign ? (
          <div className="composer-login">
            <p className="muted">{t("Log in with a signer to publish notes, replies and reactions.")}</p>
            <button type="button" className="btn btn-primary" onClick={() => setShowLogin(true)}>
              {t("Log in")}
            </button>
          </div>
        ) : (
          <>
            <div className="composer-tabs" role="tablist" aria-label={t("Composer mode")}>
              <button
                type="button"
                role="tab"
                aria-selected={tab === "edit"}
                className={`composer-tab ${tab === "edit" ? "composer-tab-active" : ""}`}
                onClick={() => setTab("edit")}
              >
                {t("Edit")}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === "preview"}
                className={`composer-tab ${tab === "preview" ? "composer-tab-active" : ""}`}
                onClick={() => setTab("preview")}
              >
                {t("Preview")}
              </button>
            </div>

            {tab === "edit" ? (
              <div className="composer-editor">
                <label className="visually-hidden" htmlFor="composer-input">
                  {t("Write a note")}
                </label>
                <textarea
                  id="composer-input"
                  ref={textareaRef}
                  className="note-composer-textarea"
                  value={content}
                  placeholder={t(PLACEHOLDER)}
                  maxLength={MAX_LENGTH}
                  autoFocus
                  onChange={(event) => setContent(event.target.value)}
                  onPaste={(event) => {
                    const files = event.clipboardData?.files
                    if (files && files.length > 0) {
                      event.preventDefault()
                      handleFiles(files)
                    }
                  }}
                  onDragOver={(event) => {
                    if (event.dataTransfer?.types.includes("Files")) event.preventDefault()
                  }}
                  onDrop={(event) => {
                    const files = event.dataTransfer?.files
                    if (files && files.length > 0) {
                      event.preventDefault()
                      handleFiles(files)
                    }
                  }}
                  onKeyDown={(event) => {
                    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                      event.preventDefault()
                      void post()
                    }
                  }}
                />

                {threadNotes.map((note, index) => (
                  <div key={index} className="composer-thread-note">
                    <textarea
                      value={note}
                      placeholder={t("Next note in the thread…")}
                      maxLength={MAX_LENGTH}
                      aria-label={`${t("Thread note")} ${index + 2}`}
                      onChange={(event) =>
                        setThreadNotes((current) =>
                          current.map((item, position) => (position === index ? event.target.value : item)),
                        )
                      }
                    />
                    <button
                      type="button"
                      className="icon-button small"
                      aria-label={t("Remove note")}
                      onClick={() => setThreadNotes((current) => current.filter((_, position) => position !== index))}
                    >
                      <Icon name="x" size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="composer-preview">
                {content.trim() ? (
                  <NoteContent event={previewEvent} />
                ) : (
                  <p className="muted">{t("Nothing to preview yet — write your note first.")}</p>
                )}
                {threadNotes.some((note) => note.trim()) && (
                  <p className="muted small">
                    {t("Thread")}: {1 + threadNotes.filter((note) => note.trim()).length} {t("notes")}
                  </p>
                )}
              </div>
            )}


            <div className="composer-meta">
              <div className="composer-meta-group">
                <button
                  type="button"
                  className={`composer-toggle ${isProtected ? "composer-toggle-on" : ""}`}
                  aria-pressed={isProtected}
                  onClick={() => setIsProtected((value) => !value)}
                  title={t("Publish as a NIP-70 protected note")}
                >
                  <Icon name="lock" size={17} /> {t("Protected")}
                </button>
                <button
                  type="button"
                  className="composer-icon-button"
                  aria-label={t("What is a protected note?")}
                  aria-expanded={showProtectedHelp}
                  onClick={() => setShowProtectedHelp((value) => !value)}
                >
                  <Icon name="help" size={17} />
                </button>
              </div>
              <div className="composer-popover-wrap composer-meta-group">
                <button
                  type="button"
                  className="composer-toggle"
                  aria-expanded={popover === "relays"}
                  onClick={() => setPopover((value) => (value === "relays" ? null : "relays"))}
                  title={t("Choose which relays receive this note")}
                >
                  <Icon name="waves" size={17} /> {relayLabel} <Icon name="chevron-down" size={14} />
                </button>
                {popover === "relays" && (
                  <div className="composer-popover composer-popover-wide" role="group" aria-label={t("Publish to relays")}>
                    <button
                      type="button"
                      className={`composer-option ${relayMode === "optimal" ? "selected" : ""}`}
                      onClick={() => {
                        setRelayMode("optimal")
                        setPopover(null)
                      }}
                    >
                      <Icon name="waves" size={15} />
                      <span>
                        {t("Optimal relays")}
                        <small>{relayCountLabel}</small>
                      </span>
                      {relayMode === "optimal" && <Icon name="check" size={15} />}
                    </button>
                    <div className="composer-option-heading">{t("Pick relays")}</div>
                    {writeTargets.length === 0 && <p className="muted small">{t("Add a relay first.")}</p>}
                    {writeTargets.map((url) => {
                      const selected = relayMode === "custom" && customRelays.includes(url)
                      return (
                        <label key={url} className="composer-relay-row">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => {
                              setRelayMode("custom")
                              setCustomRelays((current) => {
                                const next = current.includes(url)
                                  ? current.filter((item) => item !== url)
                                  : [...current, url]
                                return next.length === writeTargets.length ? [...next] : next
                              })
                            }}
                          />
                          <span>{simplifyUrl(url)}</span>
                        </label>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>

            {showProtectedHelp && (
              <p className="composer-help" role="status">
                <Icon name="lock" size={14} />
                {t(
                  "Protected notes carry a NIP-70 \"-\" tag. Relays must only share them with you and the accounts you mention, and require NIP-42 authentication before serving them.",
                )}
              </p>
            )}

            {hint && (
              <p className="composer-hint" role="status">
                <Icon name="info" size={14} /> {hint}
              </p>
            )}
          </>
        )}
      </Modal>
      <LoginDialog open={showLogin} onClose={() => setShowLogin(false)} />
    </>
  )
}

